import { useState } from 'react'
import type { LyricResult as LyricResultType } from '@creanizer/types'
import { Card } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Copy, Download, Music } from 'lucide-react'
import { LyricResultTabs } from './LyricResultTabs'

interface LyricResultProps {
  result: LyricResultType
}

export function LyricResult({ result }: LyricResultProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null)

  const handleCopy = async (text: string, section?: string) => {
    try {
      await navigator.clipboard.writeText(text)
      if (section) {
        setCopiedSection(section)
        setTimeout(() => setCopiedSection(null), 2000)
      }
      // コピー成功
    } catch (err) {
      console.error('コピーに失敗しました:', err)
    }
  }

  const handleDownload = () => {
    const text = `${result.basicInfo.title}
${result.basicInfo.genre} / ${result.basicInfo.mood}

${result.content.fullLyric}

---
Generated with CreaNizer
${new Date().toLocaleString('ja-JP')}`

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.basicInfo.title || 'lyrics'}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Card className="p-6">
      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold mb-2">
              {result.basicInfo.title}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">
                <Music className="w-3 h-3 mr-1" />
                {result.basicInfo.genre}
              </Badge>
              <Badge variant="outline">
                {result.basicInfo.mood}
              </Badge>
              <Badge variant="outline">
                {result.basicInfo.language}
              </Badge>
              {result.structure.estimatedDuration && (
                <Badge variant="outline">
                  {result.structure.estimatedDuration}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="icon"
              variant="outline"
              onClick={() => handleCopy(result.content.fullLyric)}
              title="全体をコピー"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={handleDownload}
              title="ダウンロード"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* 概要 */}
        <Card className="p-4 bg-muted/50">
          <p className="text-sm leading-relaxed">
            {result.basicInfo.concept}
          </p>
        </Card>
      </div>

      {/* タブ表示コンポーネント */}
      <LyricResultTabs 
        result={result}
        onCopy={handleCopy}
        onDownload={handleDownload}
        copiedSection={copiedSection}
      />
    </Card>
  )
}